import { ParkingRequest, SizeCategory, Spot } from "./types";

/**
 * Size category ordering from smallest to largest.
 * Used as index for waste calculation (0-4 scale).
 *
 * Index mapping:
 * - very-small: 0 (~35-40ft wingspan)
 * - small: 1 (~50-60ft)
 * - midsize: 2 (~60-70ft)
 * - super-midsize: 3 (~75-100ft)
 * - large: 4 (~100-120ft)
 */
export const SIZE_ORDER: SizeCategory[] = [
  "very-small",
  "small",
  "midsize",
  "super-midsize",
  "large",
];

/**
 * Calculate spot size waste penalty.
 *
 * Measures how many size categories larger the spot is compared to the aircraft.
 * This prevents small aircraft from "stealing" large-capable spots, reserving
 * premium capacity for aircraft that actually need it.
 *
 * **Scoring Scale (aligned with egress penalties):**
 * - Returns 0-4 (same scale as trap count)
 * - Multiplied by wasteWeight in scoring (default 1.0)
 * - Additive with egress score: totalScore = egressScore + wasteScore
 *
 * **Examples:**
 * - Citation (small=1) in small-only spot (max=1): waste = 0 ✅ Perfect fit
 * - Citation (small=1) in large-capable spot (max=4): waste = 3 ❌ Oversized
 * - G650 (large=4) in large-capable spot (max=4): waste = 0 ✅ Perfect fit
 *
 * **Edge Cases:**
 * - If spot is smaller than aircraft: returns 0 (should be filtered by size check earlier)
 * - If spot allows multiple sizes: uses maximum allowed size for calculation
 *
 * @param aircraft - Aircraft to place
 * @param spot - Candidate spot
 * @returns Waste value (0 = perfect fit, 1-4 = degrees of mismatch)
 */
export function calculateSpotWaste(
  aircraft: ParkingRequest,
  spot: Spot
): number {
  // Get aircraft size index (0-4)
  const aircraftSizeIndex = SIZE_ORDER.indexOf(aircraft.sizeCategory);

  // Get spot's maximum allowed size index (0-4)
  // Example: allowedSizes=["small", "large"] → max index is 4 (large)
  const spotMaxSizeIndex = Math.max(
    ...spot.allowedSizes.map((s) => SIZE_ORDER.indexOf(s))
  );

  // Waste = how many size categories larger the spot is
  // If spot is smaller than aircraft (shouldn't happen due to size filtering), return 0
  // Example: Citation (index 1) in large spot (index 4) → 4 - 1 = 3 waste
  return Math.max(0, spotMaxSizeIndex - aircraftSizeIndex);
}

/**
 * Get human-readable description of waste for UI display.
 *
 * Converts numeric waste value to friendly text for tooltips and decision logs.
 *
 * @param waste - Waste value from calculateSpotWaste()
 * @returns Friendly description string
 */
export function getWasteDescription(waste: number): string {
  if (waste === 0) return "Perfect fit";
  if (waste === 1) return "1 size too large";
  return `${waste} sizes too large`;
}

/**
 * Calculate structural complexity penalty for spot blocking topology.
 *
 * Returns the count of spots blocked by the BEST egress lane (minimum blocking).
 * This penalizes spots with complex egress relationships, even if no actual
 * conflict exists, encouraging the algorithm to use "clean" spots first.
 *
 * **Rationale:**
 * - Blocking 4 spots means potentially 4 aircraft moves if egress needed
 * - Better to save complex spots as last resort
 * - Preserves operational flexibility for the FBO
 *
 * **Scoring:**
 * - Returns 0 for spots with no blocking relationships
 * - Returns N for spots that block N other spots via best lane
 * - Multiply by structuralWeight (e.g., 0.1) to make it a tiebreaker
 *
 * **Examples:**
 * - Spot blocks [] → complexity = 0 (perfect, no constraints)
 * - Spot blocks [A04] → complexity = 1 (minor constraint)
 * - Spot blocks [A02, A03, A04] → complexity = 3 (major constraint)
 *
 * @param spot - Candidate spot to evaluate
 * @returns Number of spots blocked by best egress lane (0-N)
 */
export function calculateStructuralComplexity(spot: Spot): number {
  // No egress options = no blocking = no complexity
  if (!spot.egressOptions || spot.egressOptions.length === 0) {
    return 0;
  }

  // Find the lane with minimum blocked spots
  // WHY: Aircraft will choose best available lane at departure, so we penalize
  //      based on the best-case scenario (not worst-case)
  const minBlockedSpots = Math.min(
    ...spot.egressOptions.map((opt) => opt.blocksSpotIds.length)
  );

  return minBlockedSpots;
}
